import { useState } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { globalOffices, logoOptions } from '@/mocks/homeData';

function SectionReveal({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useScrollReveal();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className} ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export default function Contact() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [activeLogo, setActiveLogo] = useState(0);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const params = new URLSearchParams();

    formData.forEach((value, key) => {
      params.append(key, value.toString());
    });

    fetch('https://readdy.ai/api/form/d7novbdbmnmv4i2uk89g', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString(),
    })
      .then(() => {
        setFormStatus('success');
        form.reset();
      })
      .catch(() => {
        setFormStatus('error');
      });
  };

  return (
    <div className="bg-azosis-dark min-h-screen pt-20">
      {/* Hero */}
      <section className="py-20 md:py-32 bg-azosis-surface">
        <div className="w-full px-6 md:px-10 lg:px-16">
          <div className="max-w-4xl mx-auto text-center">
            <span className="text-azosis-gold text-xs tracking-[0.3em] uppercase mb-4 block">Contact</span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Start a Conversation
            </h1>
            <p className="text-azosis-textSecondary text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              Every engagement begins with a conversation. We listen first, document the scope with you, and produce evidence — fast — that the problem is solvable and the path is clear.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form + Offices */}
      <section className="py-20 md:py-32 bg-azosis-dark">
        <div className="w-full px-6 md:px-10 lg:px-16">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
              {/* Form */}
              <SectionReveal>
                <div>
                  <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-2">
                    Get in Touch
                  </h2>
                  <p className="text-azosis-textSecondary text-sm mb-8">
                    Fill out the form below and our team will respond within 24 hours.
                  </p>

                  <form
                    id="azosis-contact"
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="space-y-5"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="name" className="block text-azosis-textSecondary text-sm mb-2">
                          Full Name
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          className="w-full px-4 py-3 bg-azosis-surface border border-azosis-border text-white text-sm focus:border-azosis-gold/50 focus:outline-none transition-colors"
                          placeholder="Your name"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-azosis-textSecondary text-sm mb-2">
                          Email Address
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          className="w-full px-4 py-3 bg-azosis-surface border border-azosis-border text-white text-sm focus:border-azosis-gold/50 focus:outline-none transition-colors"
                          placeholder="you@company.com"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="company" className="block text-azosis-textSecondary text-sm mb-2">
                        Company
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        className="w-full px-4 py-3 bg-azosis-surface border border-azosis-border text-white text-sm focus:border-azosis-gold/50 focus:outline-none transition-colors"
                        placeholder="Your company name"
                      />
                    </div>

                    <div>
                      <label htmlFor="interest" className="block text-azosis-textSecondary text-sm mb-2">
                        Area of Interest
                      </label>
                      <select
                        id="interest"
                        name="interest"
                        className="w-full px-4 py-3 bg-azosis-surface border border-azosis-border text-white text-sm focus:border-azosis-gold/50 focus:outline-none transition-colors"
                      >
                        <option value="">Select an area</option>
                        <option value="modernize">Modernize (Migration)</option>
                        <option value="mobilize">Mobilize & Magnify (Workspace + AI)</option>
                        <option value="manage">Manage Success Practice</option>
                        <option value="general">General Inquiry</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-azosis-textSecondary text-sm mb-2">
                        Message
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={5}
                        maxLength={500}
                        required
                        className="w-full px-4 py-3 bg-azosis-surface border border-azosis-border text-white text-sm focus:border-azosis-gold/50 focus:outline-none transition-colors resize-none"
                        placeholder="Tell us about your project or challenge..."
                      ></textarea>
                      <p className="text-azosis-textMuted text-xs mt-1">Maximum 500 characters</p>
                    </div>

                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-azosis-gold text-azosis-dark font-semibold text-sm tracking-wide hover:bg-azosis-goldLight transition-all duration-300 disabled:opacity-50 whitespace-nowrap"
                    >
                      {formStatus === 'submitting' ? (
                        <>
                          <i className="ri-loader-4-line animate-spin"></i>
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message
                          <i className="ri-send-plane-line"></i>
                        </>
                      )}
                    </button>

                    {formStatus === 'success' && (
                      <div className="flex items-center gap-2 text-green-400 text-sm">
                        <i className="ri-checkbox-circle-line"></i>
                        Message sent successfully. We'll be in touch soon.
                      </div>
                    )}
                    {formStatus === 'error' && (
                      <div className="flex items-center gap-2 text-red-400 text-sm">
                        <i className="ri-error-warning-fill"></i>
                        Something went wrong. Please try again.
                      </div>
                    )}
                  </form>
                </div>
              </SectionReveal>

              {/* Offices */}
              <SectionReveal delay={150}>
                <div>
                  <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-2">
                    Our Offices
                  </h2>
                  <p className="text-azosis-textSecondary text-sm mb-8">
                    Four entities. Four hubs. One Azosis.
                  </p>

                  <div className="space-y-4">
                    {globalOffices.map((office) => (
                      <div
                        key={office.region}
                        className="bg-azosis-surface border border-azosis-border p-6 hover:border-azosis-gold/40 transition-all duration-300"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-2 h-2 bg-azosis-gold rounded-full"></div>
                          <span className="text-azosis-gold font-bold text-sm tracking-wider">{office.region}</span>
                        </div>
                        <h4 className="text-white font-semibold text-base mb-1">{office.entity}</h4>
                        <p className="text-azosis-textSecondary text-sm">{office.location}</p>
                        <p className="text-azosis-textMuted text-xs mt-2">{office.role}</p>
                      </div>
                    ))}
                  </div>

                  {/* Direct Contact */}
                  <div className="mt-8 p-6 bg-azosis-surface border border-azosis-border">
                    <h4 className="text-white font-semibold text-sm mb-4">Direct Contact</h4>
                    <div className="space-y-3">
                      <a href="mailto:pmo@azosis.com" className="flex items-center gap-3 text-azosis-textSecondary text-sm hover:text-azosis-gold transition-colors">
                        <i className="ri-mail-line text-azosis-gold"></i>
                        pmo@azosis.com
                      </a>
                      <a href="tel:+919620001297" className="flex items-center gap-3 text-azosis-textSecondary text-sm hover:text-azosis-gold transition-colors">
                        <i className="ri-phone-line text-azosis-gold"></i>
                        +91 96200 01297
                      </a>
                    </div>
                  </div>
                </div>
              </SectionReveal>
            </div>
          </div>
        </div>
      </section>

      {/* Logo Redesign Showcase */}
      <section className="py-20 md:py-32 bg-azosis-surface">
        <div className="w-full px-6 md:px-10 lg:px-16">
          <div className="max-w-6xl mx-auto">
            <SectionReveal>
              <div className="text-center mb-16">
                <span className="text-azosis-gold text-xs tracking-[0.3em] uppercase mb-4 block">Brand Identity</span>
                <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                  Logo Redesign Options
                </h2>
                <p className="text-azosis-textSecondary text-base md:text-lg max-w-2xl mx-auto">
                  Six final logo candidates for top-management vote. Each rendered with the Azosis brand architecture in mind — the equation AZ × OS × IS = AZOSIS as the guiding principle.
                </p>
              </div>
            </SectionReveal>

            {/* Logo Selector */}
            <SectionReveal delay={100}>
              <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-12">
                {logoOptions.map((logo, index) => (
                  <button
                    key={logo.id}
                    onClick={() => setActiveLogo(index)}
                    className={`px-4 py-2 text-sm tracking-wide transition-all duration-300 whitespace-nowrap ${
                      activeLogo === index
                        ? 'bg-azosis-gold text-azosis-dark font-semibold'
                        : 'bg-azosis-dark border border-azosis-border text-azosis-textSecondary hover:text-white hover:border-azosis-gold/40'
                    }`}
                  >
                    Option {logo.id}
                  </button>
                ))}
              </div>
            </SectionReveal>

            {/* Active Logo Display */}
            <SectionReveal delay={200}>
              <div className="bg-azosis-dark border border-azosis-border p-8 md:p-12 lg:p-16">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                  {/* Logo Visual */}
                  <div className="flex items-center justify-center">
                    <div className="relative">
                      {/* Logo rendering based on style */}
                      {logoOptions[activeLogo].style === 'geometric' && (
                        <div className="text-center">
                          <div className="flex items-center justify-center gap-1 mb-4">
                            {'AZOSIS'.split('').map((letter, i) => (
                              <span
                                key={i}
                                className={`font-display font-bold text-5xl md:text-6xl lg:text-7xl ${
                                  letter === 'O' ? 'text-azosis-gold' : 'text-white'
                                }`}
                              >
                                {letter === 'O' ? (
                                  <span className="inline-block w-[0.8em] h-[0.8em] border-2 border-azosis-gold rotate-45 flex items-center justify-center mt-1">
                                    <span className="w-2 h-2 bg-azosis-gold"></span>
                                  </span>
                                ) : (
                                  letter
                                )}
                              </span>
                            ))}
                          </div>
                          <div className="w-32 h-1 bg-azosis-gold mx-auto"></div>
                        </div>
                      )}

                      {logoOptions[activeLogo].style === 'minimalist' && (
                        <div className="text-center">
                          <div className="flex items-center justify-center mb-4">
                            <div className="relative">
                              <span className="font-display font-bold text-5xl md:text-6xl lg:text-7xl text-white tracking-wider">AZOSIS</span>
                              <div className="absolute -top-2 left-0 right-0 flex justify-center">
                                <div className="flex gap-1">
                                  <div className="w-1 h-3 bg-azosis-gold/60"></div>
                                  <div className="w-1 h-4 bg-azosis-gold"></div>
                                  <div className="w-1 h-3 bg-azosis-gold/60"></div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <p className="text-azosis-textMuted text-xs tracking-[0.3em] uppercase">Technologies</p>
                        </div>
                      )}

                      {logoOptions[activeLogo].style === 'dynamic' && (
                        <div className="text-center">
                          <div className="flex items-center justify-center mb-4">
                            <div className="relative w-20 h-20 md:w-24 md:h-24 mr-4">
                              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[10px] border-r-[10px] border-b-[18px] border-l-transparent border-r-transparent border-b-azosis-gold"></div>
                              <div className="absolute bottom-2 left-2 w-0 h-0 border-t-[10px] border-r-[18px] border-b-[10px] border-t-transparent border-b-transparent border-r-azosis-bronze"></div>
                              <div className="absolute bottom-2 right-2 w-0 h-0 border-t-[10px] border-l-[18px] border-b-[10px] border-t-transparent border-b-transparent border-l-azosis-gold/60"></div>
                            </div>
                            <span className="font-display font-bold text-4xl md:text-5xl lg:text-6xl text-white tracking-wider">AZOSIS</span>
                          </div>
                          <div className="w-24 h-1 bg-gradient-to-r from-azosis-gold via-azosis-bronze to-azosis-gold mx-auto"></div>
                        </div>
                      )}

                      {logoOptions[activeLogo].style === 'trinity' && (
                        <div className="text-center">
                          <div className="flex flex-col items-center gap-2 mb-4">
                            <div className="flex items-center gap-3">
                              <span className="font-display font-bold text-2xl md:text-3xl text-white px-3 py-1 border border-azosis-border">AZ</span>
                              <span className="text-azosis-gold text-xl">×</span>
                              <span className="font-display font-bold text-2xl md:text-3xl text-white px-3 py-1 border border-azosis-border">OS</span>
                              <span className="text-azosis-gold text-xl">×</span>
                              <span className="font-display font-bold text-2xl md:text-3xl text-white px-3 py-1 border border-azosis-border">IS</span>
                            </div>
                            <span className="text-azosis-gold text-2xl">=</span>
                            <span className="font-display font-bold text-4xl md:text-5xl text-azosis-gold tracking-wider">AZOSIS</span>
                          </div>
                        </div>
                      )}

                      {logoOptions[activeLogo].style === 'signature' && (
                        <div className="text-center">
                          <div className="mb-3">
                            <span className="font-display font-bold text-5xl md:text-6xl lg:text-7xl text-white tracking-wide">Azosis</span>
                          </div>
                          <div className="w-40 h-px bg-azosis-gold mx-auto mb-3"></div>
                          <p className="text-azosis-gold text-xs md:text-sm tracking-[0.2em] uppercase">
                            Three Systems · Core Engines · One Signature
                          </p>
                        </div>
                      )}

                      {logoOptions[activeLogo].style === 'monogram' && (
                        <div className="text-center">
                          <div className="flex items-center justify-center mb-4">
                            <div className="w-20 h-20 md:w-24 md:h-24 border-2 border-azosis-gold/40 flex items-center justify-center mr-4">
                              <div className="relative">
                                <span className="font-display font-bold text-3xl md:text-4xl text-azosis-gold absolute -top-3 -left-2">A</span>
                                <span className="font-display font-bold text-2xl md:text-3xl text-white absolute top-1 left-3">Z</span>
                                <span className="font-display font-bold text-xl md:text-2xl text-azosis-bronze absolute -bottom-2 -right-1">S</span>
                              </div>
                            </div>
                            <span className="font-display font-bold text-3xl md:text-4xl lg:text-5xl text-white tracking-wider">AZOSIS</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Logo Info */}
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <span className="w-8 h-8 bg-azosis-gold text-azosis-dark font-bold text-sm flex items-center justify-center">
                        {logoOptions[activeLogo].id}
                      </span>
                      <h3 className="text-white font-display text-xl font-bold">{logoOptions[activeLogo].name}</h3>
                    </div>
                    <p className="text-azosis-textSecondary text-base leading-relaxed mb-6">
                      {logoOptions[activeLogo].description}
                    </p>

                    <div className="space-y-3">
                      <h4 className="text-white font-semibold text-sm tracking-wide uppercase">Style Characteristics</h4>
                      <div className="flex flex-wrap gap-2">
                        {logoOptions[activeLogo].style === 'geometric' && ['Hexagon motif', 'Gold accent line', 'Modern sans-serif', 'System convergence'].map(tag => (
                          <span key={tag} className="px-3 py-1 bg-azosis-surfaceLight border border-azosis-border text-azosis-textSecondary text-xs">{tag}</span>
                        ))}
                        {logoOptions[activeLogo].style === 'minimalist' && ['Circuit pattern', 'Monochrome base', 'Tech-forward', 'Clean lines'].map(tag => (
                          <span key={tag} className="px-3 py-1 bg-azosis-surfaceLight border border-azosis-border text-azosis-textSecondary text-xs">{tag}</span>
                        ))}
                        {logoOptions[activeLogo].style === 'dynamic' && ['Converging arrows', 'Gradient fill', 'Abstract A icon', 'Motion implied'].map(tag => (
                          <span key={tag} className="px-3 py-1 bg-azosis-surfaceLight border border-azosis-border text-azosis-textSecondary text-xs">{tag}</span>
                        ))}
                        {logoOptions[activeLogo].style === 'trinity' && ['Equation format', 'Three blocks', 'Mathematical', 'Systematic'].map(tag => (
                          <span key={tag} className="px-3 py-1 bg-azosis-surfaceLight border border-azosis-border text-azosis-textSecondary text-xs">{tag}</span>
                        ))}
                        {logoOptions[activeLogo].style === 'signature' && ['Serif elegance', 'Tagline integrated', 'Established feel', 'Premium positioning'].map(tag => (
                          <span key={tag} className="px-3 py-1 bg-azosis-surfaceLight border border-azosis-border text-azosis-textSecondary text-xs">{tag}</span>
                        ))}
                        {logoOptions[activeLogo].style === 'monogram' && ['Single mark', 'Scalable', 'Geometric letters', 'Instant recognition'].map(tag => (
                          <span key={tag} className="px-3 py-1 bg-azosis-surfaceLight border border-azosis-border text-azosis-textSecondary text-xs">{tag}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </SectionReveal>

            {/* All Options Grid */}
            <SectionReveal delay={300}>
              <div className="mt-12">
                <h3 className="text-white font-semibold text-sm tracking-wide uppercase mb-6 text-center">
                  All Options at a Glance
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {logoOptions.map((logo, index) => (
                    <button
                      key={logo.id}
                      onClick={() => setActiveLogo(index)}
                      className={`bg-azosis-dark border p-4 text-center transition-all duration-300 ${
                        activeLogo === index
                          ? 'border-azosis-gold'
                          : 'border-azosis-border hover:border-azosis-gold/40'
                      }`}
                    >
                      <div className="w-10 h-10 bg-azosis-surface border border-azosis-border flex items-center justify-center mx-auto mb-3">
                        <span className={`font-display font-bold text-lg ${activeLogo === index ? 'text-azosis-gold' : 'text-white'}`}>
                          {logo.id}
                        </span>
                      </div>
                      <p className={`text-xs ${activeLogo === index ? 'text-azosis-gold' : 'text-azosis-textSecondary'}`}>
                        {logo.name}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            </SectionReveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-azosis-dark">
        <div className="w-full px-6 md:px-10 lg:px-16">
          <div className="max-w-3xl mx-auto text-center">
            <SectionReveal>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6">
                Ready to Explore Our Solutions?
              </h2>
              <p className="text-azosis-textSecondary text-base md:text-lg mb-10">
                Whether you are exploring, validating, or ready to commit — we have a path for you.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="mailto:pmo@azosis.com"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-azosis-gold text-azosis-dark font-semibold text-sm tracking-wide hover:bg-azosis-goldLight transition-all duration-300 whitespace-nowrap"
                >
                  <i className="ri-mail-line"></i>
                  Email Us
                </a>
                <a
                  href="tel:+919620001297"
                  className="inline-flex items-center gap-2 px-8 py-4 border border-azosis-border text-white text-sm tracking-wide hover:border-azosis-gold/50 hover:text-azosis-gold transition-all duration-300 whitespace-nowrap"
                >
                  <i className="ri-phone-line"></i>
                  +91 96200 01297
                </a>
              </div>
            </SectionReveal>
          </div>
        </div>
      </section>
    </div>
  );
}